import { useState, type ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { CONDITION_COLORS, CONDITION_RATINGS } from '@redevicer/shared'
import { conditionLabel as formatConditionLabel } from '../utils/conditionLabel'
import { USAGE_DURATION_ORDER } from '../utils/usageDuration'
import { IN_BOX_OPTIONS, inBoxLabelKey } from '../utils/inBoxCompleteness'
import CollapseSlot from './CollapseSlot'

const BATTERY_MIN_OPTIONS = ['', '80', '90', '95'] as const

function batteryFilterLabel(min: (typeof BATTERY_MIN_OPTIONS)[number], t: TFunction): string {
  if (!min) return t('catalog.anyBattery')
  return t(`catalog.batteryMin${min}`)
}

export type ListingCriteria = {
  condition: string
  warranty: '' | 'yes' | 'no'
  usage: string
  batteryMin: '' | '80' | '90' | '95'
  inBox: string
  city: string
}

type Props = {
  value: ListingCriteria
  onChange: (next: ListingCriteria) => void
  className?: string
  /** Catalog: dropdown fields + collapsible advanced filters */
  compact?: boolean
  /** Inline with catalog filter panel — shared pill controls */
  unified?: boolean
  cityOptions?: string[]
}

const CONDITIONS = CONDITION_RATINGS

function conditionColor(condition: string): string {
  return CONDITION_COLORS[condition as keyof typeof CONDITION_COLORS] ?? '#6b7280'
}

function CriteriaSelect({
  id,
  label,
  value,
  onChange,
  children,
  unified = false,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
  unified?: boolean
}) {
  return (
    <div className={unified ? 'catalog-filter-field' : 'listing-criteria-field'}>
      <label className={unified ? 'catalog-filter-field__label' : 'listing-criteria-field-label'} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={unified ? 'catalog-filter-control catalog-filter-control--select' : 'listing-criteria-select'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </select>
    </div>
  )
}

/** Filter groups: condition / warranty / usage — catalog uses compact dropdowns. */
export default function ListingCriteriaBar({
  value,
  onChange,
  className = '',
  compact = false,
  unified = false,
  cityOptions = [],
}: Props) {
  const { t } = useTranslation()
  const [moreOpen, setMoreOpen] = useState(false)
  const hasAdvanced = Boolean(value.warranty || value.usage || value.batteryMin || value.inBox)

  if (compact) {
    const advancedFields = (
      <CollapseSlot open={moreOpen}>
      <div
        className={
          unified
            ? 'catalog-filters-panel__advanced catalog-filters-panel__row catalog-filters-panel__row--quad'
            : 'listing-criteria-advanced-fields'
        }
      >
        <CriteriaSelect
          unified={unified}
          id="catalog-filter-warranty"
          label={t('catalog.filterWarranty')}
          value={value.warranty}
          onChange={(warranty) => onChange({ ...value, warranty: warranty as '' | 'yes' | 'no' })}
        >
          <option value="">{t('catalog.anyWarranty')}</option>
          <option value="yes">{t('listing.warrantyYes')}</option>
          <option value="no">{t('listing.warrantyNo')}</option>
        </CriteriaSelect>

        <CriteriaSelect
          unified={unified}
          id="catalog-filter-usage"
          label={t('catalog.filterUsage')}
          value={value.usage}
          onChange={(usage) => onChange({ ...value, usage })}
        >
          <option value="">{t('catalog.anyUsage')}</option>
          {USAGE_DURATION_ORDER.filter((k) => k !== 'unknown').map((key) => (
            <option key={key} value={key}>
              {t(`listing.usage.${key}`)}
            </option>
          ))}
        </CriteriaSelect>

        <CriteriaSelect
          unified={unified}
          id="catalog-filter-battery"
          label={t('catalog.filterBattery')}
          value={value.batteryMin}
          onChange={(batteryMin) => onChange({ ...value, batteryMin: batteryMin as '' | '80' | '90' | '95' })}
        >
          {BATTERY_MIN_OPTIONS.map((min) => (
            <option key={min || 'any'} value={min}>
              {batteryFilterLabel(min, t)}
            </option>
          ))}
        </CriteriaSelect>

        <CriteriaSelect
          unified={unified}
          id="catalog-filter-kit"
          label={t('listing.kitLegend')}
          value={value.inBox}
          onChange={(inBox) => onChange({ ...value, inBox })}
        >
          <option value="">{t('catalog.anyKit')}</option>
          {IN_BOX_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(inBoxLabelKey(key))}
            </option>
          ))}
        </CriteriaSelect>
      </div>
      </CollapseSlot>
    )

    const moreToggle = (
      <button
        type="button"
        className={
          unified
            ? `catalog-filters-panel__more${hasAdvanced ? ' catalog-filters-panel__more--active' : ''}`
            : `listing-criteria-more-toggle${hasAdvanced ? ' listing-criteria-more-toggle--active' : ''}`
        }
        onClick={() => setMoreOpen((v) => !v)}
        aria-expanded={moreOpen}
      >
        {moreOpen ? '▲' : '▼'} {t('catalog.moreFilters')}
        {hasAdvanced && !moreOpen ? ' •' : ''}
      </button>
    )

    if (unified) {
      return (
        <>
          <div className="catalog-filters-panel__row catalog-filters-panel__row--duo">
            <CriteriaSelect
              unified
              id="catalog-filter-city"
              label={t('catalog.filterCity')}
              value={value.city}
              onChange={(city) => onChange({ ...value, city })}
            >
              <option value="">{t('catalog.anyCity')}</option>
              {cityOptions.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </CriteriaSelect>

            <CriteriaSelect
              unified
              id="catalog-filter-condition"
              label={t('catalog.filterCondition')}
              value={value.condition}
              onChange={(condition) => onChange({ ...value, condition })}
            >
              <option value="">{t('catalog.allConditions')}</option>
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {formatConditionLabel(c, t)}
                </option>
              ))}
            </CriteriaSelect>
          </div>

          <div className="catalog-filters-panel__extras">
            {moreToggle}
            {advancedFields}
          </div>
        </>
      )
    }

    return (
      <div className={`listing-criteria-bar listing-criteria-bar--compact listing-criteria-bar--dropdown ${className}`.trim()}>
        <CriteriaSelect
          id="catalog-filter-city"
          label={t('catalog.filterCity')}
          value={value.city}
          onChange={(city) => onChange({ ...value, city })}
        >
          <option value="">{t('catalog.anyCity')}</option>
          {cityOptions.map((city) => (
            <option key={city} value={city}>
              {city}
            </option>
          ))}
        </CriteriaSelect>

        <CriteriaSelect
          id="catalog-filter-condition"
          label={t('catalog.filterCondition')}
          value={value.condition}
          onChange={(condition) => onChange({ ...value, condition })}
        >
          <option value="">{t('catalog.allConditions')}</option>
          {CONDITIONS.map((c) => (
            <option key={c} value={c}>
              {formatConditionLabel(c, t)}
            </option>
          ))}
        </CriteriaSelect>

        {moreToggle}
        {advancedFields}
      </div>
    )
  }

  const conditionBlock = (
    <fieldset className="listing-criteria-group listing-criteria-group--primary">
      <legend className="listing-criteria-legend">{t('catalog.filterCondition')}</legend>
      <div className="listing-criteria-row" role="group" aria-label={t('catalog.filterCondition')}>
        <button
          type="button"
          className={`condition-chip${value.condition === '' ? ' condition-chip--active condition-chip--filter-on' : ''}`}
          onClick={() => onChange({ ...value, condition: '' })}
        >
          {t('catalog.allConditions')}
        </button>
        {CONDITIONS.map((c) => (
          <button
            key={c}
            type="button"
            className={`condition-chip${value.condition === c ? ' condition-chip--active' : ''}`}
            style={
              value.condition === c
                ? { backgroundColor: conditionColor(c), borderColor: conditionColor(c), color: '#fff' }
                : { borderColor: conditionColor(c), color: conditionColor(c) }
            }
            onClick={() => onChange({ ...value, condition: c })}
          >
            {formatConditionLabel(c, t)}
          </button>
        ))}
      </div>
    </fieldset>
  )

  const advancedBlock = (
    <>
      <fieldset className="listing-criteria-group">
        <legend className="listing-criteria-legend">{t('catalog.filterWarranty')}</legend>
        <div className="listing-criteria-row" role="group" aria-label={t('catalog.filterWarranty')}>
          <button
            type="button"
            className={`condition-chip condition-chip--filter${value.warranty === '' ? ' condition-chip--filter-on' : ''}`}
            onClick={() => onChange({ ...value, warranty: '' })}
          >
            {t('catalog.anyWarranty')}
          </button>
          <button
            type="button"
            className={`condition-chip condition-chip--filter${value.warranty === 'yes' ? ' condition-chip--filter-on' : ''}`}
            onClick={() => onChange({ ...value, warranty: 'yes' })}
          >
            {t('listing.warrantyYes')}
          </button>
          <button
            type="button"
            className={`condition-chip condition-chip--filter${value.warranty === 'no' ? ' condition-chip--filter-on' : ''}`}
            onClick={() => onChange({ ...value, warranty: 'no' })}
          >
            {t('listing.warrantyNo')}
          </button>
        </div>
      </fieldset>

      <fieldset className="listing-criteria-group">
        <legend className="listing-criteria-legend">{t('catalog.filterUsage')}</legend>
        <div className="listing-criteria-row" role="group" aria-label={t('catalog.filterUsage')}>
          <button
            type="button"
            className={`condition-chip condition-chip--filter${value.usage === '' ? ' condition-chip--filter-on' : ''}`}
            onClick={() => onChange({ ...value, usage: '' })}
          >
            {t('catalog.anyUsage')}
          </button>
          {USAGE_DURATION_ORDER.filter((k) => k !== 'unknown').map((key) => (
            <button
              key={key}
              type="button"
              className={`condition-chip condition-chip--filter${value.usage === key ? ' condition-chip--filter-on' : ''}`}
              onClick={() => onChange({ ...value, usage: key })}
            >
              {t(`listing.usage.${key}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="listing-criteria-group">
        <legend className="listing-criteria-legend">{t('catalog.filterBattery')}</legend>
        <div className="listing-criteria-row" role="group" aria-label={t('catalog.filterBattery')}>
          {BATTERY_MIN_OPTIONS.map((min) => (
            <button
              key={min || 'any'}
              type="button"
              className={`condition-chip condition-chip--filter${value.batteryMin === min ? ' condition-chip--filter-on' : ''}`}
              onClick={() => onChange({ ...value, batteryMin: min })}
            >
              {batteryFilterLabel(min, t)}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="listing-criteria-group">
        <legend className="listing-criteria-legend">{t('listing.kitLegend')}</legend>
        <div className="listing-criteria-row" role="group" aria-label={t('listing.kitLegend')}>
          <button
            type="button"
            className={`condition-chip condition-chip--filter${value.inBox === '' ? ' condition-chip--filter-on' : ''}`}
            onClick={() => onChange({ ...value, inBox: '' })}
          >
            {t('catalog.anyKit')}
          </button>
          {IN_BOX_OPTIONS.map((key) => (
            <button
              key={key}
              type="button"
              className={`condition-chip condition-chip--filter${value.inBox === key ? ' condition-chip--filter-on' : ''}`}
              onClick={() => onChange({ ...value, inBox: key })}
            >
              {t(inBoxLabelKey(key))}
            </button>
          ))}
        </div>
      </fieldset>
    </>
  )

  return (
    <div className={`listing-criteria-bar ${className}`.trim()}>
      {cityOptions.length > 0 ? (
        <fieldset className="listing-criteria-group">
          <legend className="listing-criteria-legend">{t('catalog.filterCity')}</legend>
          <div className="listing-criteria-row" role="group" aria-label={t('catalog.filterCity')}>
            <button
              type="button"
              className={`condition-chip condition-chip--filter${value.city === '' ? ' condition-chip--filter-on' : ''}`}
              onClick={() => onChange({ ...value, city: '' })}
            >
              {t('catalog.anyCity')}
            </button>
            {cityOptions.map((city) => (
              <button
                key={city}
                type="button"
                className={`condition-chip condition-chip--filter${value.city === city ? ' condition-chip--filter-on' : ''}`}
                onClick={() => onChange({ ...value, city })}
              >
                {city}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      {conditionBlock}
      {advancedBlock}
    </div>
  )
}
